/**
 * One group's editor (D14): name, permissions (with human labels), scope, and
 * members. The draft is the container's (it is what D35's live banner keeps
 * or replaces, `useLiveResource`); this renders it and emits changes. Members
 * change at once (each add or remove is its own request), apart from the
 * draft; the Admin group keeps its admin permissions, stays tenant-wide, and
 * cannot be deleted.
 */
import type { PermissionId } from '@acp/api-contract/permissions.js';
import type { Group } from '@acp/api-contract/tenancy.js';
import { useState } from 'react';
import type { PermissionInfo } from '../../lib/admin/permissions.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { PermissionChecklist } from './PermissionChecklist.js';
import { type ScopeChoice, ScopeEditor } from './ScopeEditor.js';

export type GroupDraft = { readonly name: string; readonly permissions: readonly PermissionId[]; readonly scope: Group['scope'] };

export type GroupMember = { readonly userId: string; readonly email: string };

export type GroupEditorProps = {
  readonly groupId: string;
  readonly draft: GroupDraft;
  readonly permissions: readonly PermissionInfo[];
  /** Permissions this group must keep (the Admin group's), and whether it must stay tenant-wide. */
  readonly locked: readonly PermissionId[];
  readonly protectedGroup: boolean;
  readonly sites: readonly ScopeChoice[];
  readonly studios: readonly ScopeChoice[];
  readonly members: readonly GroupMember[];
  /** Users who are not members: what "Add member" offers. */
  readonly candidates: readonly GroupMember[];
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly memberPending: boolean;
  /** How many times the page refreshed in place (a flash per refresh, D35). */
  readonly refreshes: number;
  readonly onDraftChange: (draft: GroupDraft) => void;
  readonly onSave: () => void;
  readonly onReset: () => void;
  readonly onDelete: () => void;
  readonly onAddMember: (userId: string) => void;
  readonly onRemoveMember: (userId: string) => void;
};

function Members({ members, candidates, pending, onAdd, onRemove }: { readonly members: readonly GroupMember[]; readonly candidates: readonly GroupMember[]; readonly pending: boolean; readonly onAdd: (userId: string) => void; readonly onRemove: (userId: string) => void }) {
  const [pick, setPick] = useState('');
  const chosen = candidates.some((c) => c.userId === pick) ? pick : (candidates[0]?.userId ?? '');
  return (
    <section className="grid gap-2" data-testid="group-members">
      <h3 className="text-sm font-medium">Members ({members.length})</h3>
      {members.length === 0 ? <p className="text-sm text-muted-foreground">No members.</p> : null}
      <ul className="grid gap-1">
        {members.map((m) => (
          <li key={m.userId} className="flex items-center justify-between gap-2 text-sm" data-testid="group-member">
            {m.email}
            <Button size="sm" variant="ghost" disabled={pending} onClick={() => onRemove(m.userId)} data-testid="remove-member">
              Remove
            </Button>
          </li>
        ))}
      </ul>
      {candidates.length > 0 ? (
        <div className="flex items-end gap-2">
          <div className="grid flex-1 gap-1">
            <Label htmlFor="add-member">Add member</Label>
            <NativeSelect id="add-member" value={chosen} onChange={(e) => setPick(e.target.value)}>
              {candidates.map((c) => (
                <option key={c.userId} value={c.userId}>
                  {c.email}
                </option>
              ))}
            </NativeSelect>
          </div>
          <Button variant="outline" disabled={pending || chosen === ''} onClick={() => onAdd(chosen)} data-testid="add-member">
            Add
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export function GroupEditor(props: GroupEditorProps) {
  const { draft, onDraftChange } = props;
  const nameOk = draft.name.trim() !== '' && draft.name.trim().length <= 100;
  return (
    <Card className="relative" data-testid="group-editor" data-refreshes={props.refreshes}>
      {props.refreshes > 0 ? <span key={props.refreshes} aria-hidden="true" className="pointer-events-none absolute inset-0 animate-live-flash rounded-xl ring-2 ring-success" data-testid="live-flash" /> : null}
      <CardHeader>
        <CardTitle>{draft.name.trim() === '' ? props.groupId : draft.name}</CardTitle>
        <CardDescription className="font-mono text-xs">{props.groupId}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="grid max-w-md gap-2">
          <Label htmlFor="group-name">Name</Label>
          <Input id="group-name" value={draft.name} onChange={(e) => onDraftChange({ ...draft, name: e.target.value })} data-testid="group-name" />
        </div>
        <PermissionChecklist permissions={props.permissions} selected={draft.permissions} locked={props.locked} disabled={props.saving} onChange={(permissions) => onDraftChange({ ...draft, permissions })} />
        <ScopeEditor scope={draft.scope} sites={props.sites} studios={props.studios} tenantOnly={props.protectedGroup} disabled={props.saving} onChange={(scope) => onDraftChange({ ...draft, scope })} />
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={props.onSave} disabled={!props.dirty || !nameOk || props.saving} data-testid="save-group">
            {props.saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" onClick={props.onReset} disabled={!props.dirty || props.saving}>
            Undo changes
          </Button>
          {props.protectedGroup ? null : (
            <Button variant="ghost" className="ml-auto text-destructive" onClick={props.onDelete} data-testid="delete-group">
              Delete group
            </Button>
          )}
        </div>
        <Members members={props.members} candidates={props.candidates} pending={props.memberPending} onAdd={props.onAddMember} onRemove={props.onRemoveMember} />
      </CardContent>
    </Card>
  );
}
